// import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClearFilter } from '../../../../assets/icons/filter-close.svg';
// import { ReactComponent as Filter } from '../../../../assets/icons/filter.svg';
// import { filterType } from '../../../../config/enum';
import {} from // CommonFilterProperties,
// FilterUpdateProperties,
'../../../../interface/filter.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
// import CustomPopover from '../../../../shared-components/custom-pop-over/CustomPopover';
// import CustomTableFilterV2 from '../../../../shared-components/custom-table/components/TableFiltersV2';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import endConsumerV2Api from '../../../../utils/api/end-consumer-v2';

/**
 * API activity log item
 */
interface ActivityApiModel {
  _id: string;
  title: string;
  message: string;
  status: 'read' | 'unread';
  type: 'activity';
  userCustomId: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * UI timeline activity
 */
interface TimelineActivity {
  id: string;
  title: string;
  description: string;
  timestamp: string;
}

/**
 * Maps backend activity to UI model
 * @param {any} activity - The backend activity object to map
 */
const mapActivityToTimeline = (activity: ActivityApiModel): TimelineActivity => ({
  id: activity._id,
  title: activity.title,
  description: activity.message,
  timestamp: activity.createdAt,
});

/**
 * Activity Timeline Component
 * Renders a list of activities in a vertical timeline.
 */
function ActivityTimeline(): JSX.Element {
  const { t } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [activities, setActivities] = useState<TimelineActivity[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);

  /**
   * Function to remove filter from the list of selected filters
   */
  const handleClearFilters = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Handle date change
   * @param {string} name name of the date range
   * @param {string} value value of the date range
   */
  const handleDateChange = (name: string, value: string): void => {
    setDateRange({
      ...dateRange,
      [name]: value,
    });
  };

  /**
   * Activities
   */
  useEffect(() => {
    let isMounted = true;

    /**
     * Activities
     */
    const fetchActivities = async (): Promise<void> => {
      const userCustomId = selectedEndConsumer?.userCustomId;
      if (!userCustomId) return;

      // 🚫 If only one date is selected, do not call API
      if (
        (dateRange.startDate && !dateRange.endDate) ||
        (!dateRange.startDate && dateRange.endDate)
      ) {
        return;
      }

      try {
        setLoading(true);

        const query: any = {};

        // 📅 Add dates only if BOTH exist
        if (dateRange.startDate && dateRange.endDate) {
          query.startDate = dateRange.startDate;
          query.endDate = dateRange.endDate;
        }

        const response = await endConsumerV2Api.fetchActivityLogs(userCustomId, query);

        if (isMounted && response?.data) {
          const mappedActivities = (response.data as ActivityApiModel[]).map(mapActivityToTimeline);
          setActivities(mappedActivities);
        }
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchActivities();

    return () => {
      isMounted = false;
    };
  }, [selectedEndConsumer?.userCustomId, dateRange.startDate, dateRange.endDate]);

  if (loading) {
    return (
      <div className="activity-timeline-card">
        <div className="loading-container">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="activity-timeline-card">
      <div className="activity-timeline-filter-wrap">
        <div className="activity-timeline-filter">
          <CustomDatePicker
            inputLabel={t('startDate')}
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleDateChange}
          />
          <CustomDatePicker
            inputLabel={t('endDate')}
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleDateChange}
          />
        </div>
        <div className="activity-clear-filter">
          <p> </p>
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={handleClearFilters}
            svgIcon={<ClearFilter />}
            hoverLabel={t('clearFilter')}
          />
        </div>
      </div>
      <div className="timeline-container">
        {activities.length === 0 ? (
          <div className="no-activity">{t('noActivitylogs')}</div>
        ) : (
          <div className="timeline-list">
            {activities.map((activity, index) => {
              return (
                <div key={activity.id} className="timeline-item">
                  <div className="timeline-marker">
                    <div className="dot success" />
                    {index < activities.length - 1 && <div className="line" />}
                  </div>

                  <div className="timeline-content">
                    <div className="title-row">
                      <div className="title">{t(activity.title)}</div>
                      <div className="timestamp">{ConvertUTCtoDate(activity.timestamp)}</div>
                    </div>

                    {activity.description && (
                      <div className="description">{t(activity.description)}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityTimeline;
